import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Form,
  Label,
  Input,
  Button,
  Spinner,
  Alert,
  Collapse, // Import Collapse for toggling advanced options
} from "reactstrap";
import { useTranslation } from "react-i18next";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

const NewsDataScraper = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [articleData, setArticleData] = useState(null); // This will hold data for a single article
  const [error, setError] = useState("");

  // New states for filters
  const [maxRequestRetries, setMaxRequestRetries] = useState(3);
  const [addCredentials, setAddCredentials] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [advancedOptionsOpen, setAdvancedOptionsOpen] = useState(false); // State for advanced options collapse

  document.title = "Alitqan | News";

  const handleFetchData = async () => {
    if (!url) {
      setError("Please enter a URL.");
      return;
    }

    setLoading(true);
    setError("");
    setArticleData(null);

    // Prepare options to be sent with the request (for demonstration)
    const requestOptions = {
      url,
      maxRequestRetries,
      credentials: addCredentials ? { username, password } : undefined,
    };
    console.log("Fetching with options:", requestOptions); // Log options for debugging

    try {
      await new Promise((resolve) => setTimeout(resolve, 1500)); // Simulate network delay

      // Simulate fetching and parsing the provided JSON
      const fetchedJsonData = {
        "data": {
            "count": 10,
            "desc": false,
            "items": [
                {
                    "url": "https://timeskuwait.com/environmental-police-record-4856-violations-and-cases-in-six-months/",
                    "timestamp": "2025-11-11T09:45:48.200Z",
                    "content": "A security source said the Environmental Police recorded a total of 4,856 cases, reports during field visits over the past six months, including 1,332 misdemeanor cases.\n\nThe source confirmed that serious environmental violations may lead to deportation for expatriates and imprisonment or fines for citizens.\n\nThe source explained that, as part of the Ministry of Interior’s efforts to enforce environmental protection laws and curb waste dumping in public, tourist, and desert areas, the Public Security Sector’s Environmental Police carried out intensive field inspections that resulted in the arrest of violators.\n\n_The report detailed that the Environmental Police recorded:_\n\n*   **2,151 environmental violations,**\n*   **1,332 misdemeanor cases,**\n*   **1,035 field visits to locations where waste dumping was observed, and**\n*   **347 public reports and complaints, mostly related to the illegal disposal of waste**.\n    \n    The source added that specialized field teams, including female officers, are conducting round-the-clock patrols across tourist, recreational, and desert areas to monitor compliance and deter offenders.\n    \n\nHe also noted that there is ongoing coordination between the Environmental Police, Environment Public Authority, and Kuwait Municipality to implement a joint plan targeting illegal waste dumping and environmental damage in non-designated areas.\n\nThe source emphasized that repeated or severe environmental offenses will face strict action — deportation for expatriates, or legal cases against citizens carrying penalties ranging from fines to imprisonment, depending on the nature of the violation.\n\nHe concluded by stressing that authorities will show zero tolerance toward anyone who discards waste in roads, coastal, or desert areas, adding that additional patrols will soon intensify monitoring in desert regions, particularly against those who light fires or harm the natural environment without observing safety and environmental guidelines.\n\n* * *\n\n**Follow The Times Kuwait on**  \n**[X](https://x.com/thetimesq8), [Instagram](https://www.instagram.com/thetimeskuwait/) and [Facebook](https://www.facebook.com/timeskuwait) for the latest news updates**\n\n* * *\n\n* * *\n\n**Follow The Times Kuwait on** **[X](https://x.com/thetimesq8), [Instagram](https://www.instagram.com/thetimeskuwait/) and [Facebook](https://www.facebook.com/timeskuwait) for the latest news updates**\n\n* * *\n\n  \n  \n\n[![](https://timeskuwait.com/wp-content/uploads/2025/02/Lulu24.jpg)](https://timeskuwait.com/linkout/36579)",
                    "title": "Environmental Police record 4,856 violations and cases in six months - Times Kuwait"
                },  
                {
                    "url": "https://example.com/another-article",
                    "timestamp": "2025-11-10T10:00:00.000Z",
                    "content": "This is the content of another example article, demonstrating various details about a different topic. It could be about technology, politics, or any other news worthy subject. The content would typically be much longer and provide detailed information, analysis, and quotes from relevant sources. This example is just a placeholder to show the structure.",
                    "title": "Another Example Article Title"
                },
                {
                    "url": "https://example.com/third-article",
                    "timestamp": "2025-11-09T11:30:00.000Z",
                    "content": "A third article example with content focusing on global events. This content would discuss international relations, economic impacts, and the perspectives of various nations involved. It highlights the importance of timely and accurate news reporting.",
                    "title": "Global Events and Their Impact"
                }
            ]
        }
      };

      // Filter for the article matching the URL, or just take the first one if no exact match (for simulation)
      const targetArticle = fetchedJsonData.data.items.find(item => item.url === url) || fetchedJsonData.data.items[0];

      if (targetArticle) {
        // Extract the image URL using a regex (simplified for this example)
        const imageUrlMatch = targetArticle.content.match(/\[!\[\]\((https?:\/\/[^)]+\.(?:jpg|png|gif|jpeg|webp))\)/);
        const extractedImageUrl = imageUrlMatch ? imageUrlMatch[1] : null;

        setArticleData({
          title: targetArticle.title,
          // 'author' is not directly in your JSON, you might need to extract it from content or add it to the JSON
          author: "Unknown Author", // Placeholder
          publishedDate: new Date(targetArticle.timestamp).toLocaleDateString(),
          sourceUrl: targetArticle.url,
          content: targetArticle.content.replace(/\[!\[\]\(https?:\/\/[^)]+\)\]\(https?:\/\/[^)]+\)%22,/, '').replace(/\* \* \*\s*\n\n\*\*Follow The Times Kuwait on\*\*[\s\S]*$/, ''), // Clean up content
          imageUrl: extractedImageUrl || "https://via.placeholder.com/400x200?text=No+Image", // Default image if not found
        });
      } else {
        setError("No article found for the given URL.");
      }

    } catch (err) {
      setError(err.message || "An unexpected error occurred during scraping.");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setUrl("");
    setArticleData(null);
    setError("");
    // Reset filters
    setMaxRequestRetries(3);
    setAddCredentials(false);
    setUsername("");
    setPassword("");
    setAdvancedOptionsOpen(false);
  };

  const handleCopyJson = () => {
    if (articleData) {
      navigator.clipboard.writeText(JSON.stringify(articleData, null, 2));
      alert("Article data copied to clipboard as JSON!");
    }
  };

  const handleSave = () => {
    if (articleData) {
      // Implement your save logic here
      console.log("Saving article data:", articleData);
      alert("Article data saved!");
    }
  };

  const handleDelete = () => {
    if (articleData) {
      // Implement your delete logic here
      console.log("Deleting article data:", articleData);
      setArticleData(null); // Clear displayed article after "delete"
      alert("Article data deleted!");
    }
  };

  // Helper function to render markdown-like content (simplified)
  const renderContent = (markdownContent) => {
    if (!markdownContent) return null;

    // Basic markdown to JSX conversion (you might want a dedicated library like 'marked' or 'react-markdown')
    let htmlContent = markdownContent
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold
      .replace(/\*(.*?)\*/g, '<em>$1</em>')     // Italic
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>') // Links
      .replace(/\n/g, '<br />'); // Newlines

    // Handle bullet points
    htmlContent = htmlContent.replace(/<br \/>\*&nbsp;&nbsp;/, '<ul><li>').replace(/\*&nbsp;&nbsp;/g, '</li><li>').replace(/<br \/>/g, '</li></ul>');


    return <div dangerouslySetInnerHTML={{ __html: htmlContent }} />;
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Row className="mb-3">
            <Col>
              <BreadCrumb title={t("NewsDataScraper")} />
            </Col>
          </Row>

          <Row className="justify-content-center">
            <Col xs={12}>
              <Card className="shadow-lg rounded-3 border-0 mb-4">
                <CardHeader className="text-white rounded-top-3 p-3">
                  <Row className="align-items-center">
                    <Col>
                      <h5 className="mb-0 fs-5">{t("Enter News Article URL")}</h5>
                    </Col>
                    <Col className="col-auto">
                      <Button
                        className="btn btn-soft"
                        onClick={() => navigate(-1)}
                        disabled={loading}
                      >
                        {t("Back")}
                      </Button>
                    </Col>
                  </Row>
                </CardHeader>
                <CardBody className="p-4">
                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleFetchData();
                    }}
                  >
                    <div className="d-flex flex-column flex-md-row align-items-stretch mb-3">
                      <Input
                        type="url"
                        className="form-control flex-grow-1 me-md-2 mb-2 mb-md-0 rounded-pill shadow-sm py-2 px-3"
                        placeholder={t("Paste news article URL here...")}
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        required
                      />
                      <Button
                        color="secondary"
                        className="rounded-pill shadow-sm fw-semibold px-4"
                        onClick={handleFetchData}
                        disabled={loading}
                        style={{ minWidth: "120px" }}
                      >
                        {loading ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            {t("Fetching...")}
                          </>
                        ) : (
                          t("Fetch Data")
                        )}
                      </Button>
                    </div>

                    {/* Advanced Options Toggle */}
                    <div className="text-end mb-3">
                      <Button
                        color="link"
                        onClick={() => setAdvancedOptionsOpen(!advancedOptionsOpen)}
                        aria-expanded={advancedOptionsOpen}
                        aria-controls="advanced-options-collapse"
                        className="text-decoration-none p-0"
                      >
                        {t("Advanced Options")}
                        <i className={`ms-2 ri-arrow-down-s-line ${advancedOptionsOpen ? "rotate-180" : ""}`} />
                      </Button>
                    </div>

                    {/* Advanced Options Collapse */}
                    <Collapse isOpen={advancedOptionsOpen} id="advanced-options-collapse">
                      <Card className="mb-3 p-3 border shadow-sm">
                        <h6 className="mb-3">{t("Scraping Settings")}</h6>
                        <Row className="mb-3 align-items-center">
                          <Col md={6}>
                            <Label htmlFor="maxRequestRetries" className="form-label mb-0">
                              {t("Max Request Retries")}
                            </Label>
                          </Col>
                          <Col md={6}>
                            <Input
                              type="number"
                              id="maxRequestRetries"
                              className="form-control"
                              value={maxRequestRetries}
                              onChange={(e) => setMaxRequestRetries(Number(e.target.value))}
                              min="0"
                            />
                          </Col>
                        </Row>

                        <div className="form-check form-switch mb-3">
                          <Input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="addCredentialsSwitch"
                            checked={addCredentials}
                            onChange={(e) => setAddCredentials(e.target.checked)}
                          />
                          <Label className="form-check-label" htmlFor="addCredentialsSwitch">
                            {t("Add Credentials")}
                          </Label>
                        </div>

                        <Collapse isOpen={addCredentials}>
                          <Row className="mb-2">
                            <Col md={6}>
                              <Label htmlFor="username" className="form-label">
                                {t("Username")}
                              </Label>
                              <Input
                                type="text"
                                id="username"
                                className="form-control"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                placeholder="Enter username"
                              />
                            </Col>
                            <Col md={6}>
                              <Label htmlFor="password" className="form-label">
                                {t("Password")}
                              </Label>
                              <Input
                                type="password"
                                id="password"
                                className="form-control"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter password"
                              />
                            </Col>
                          </Row>
                        </Collapse>
                      </Card>
                    </Collapse>


                    <div className="d-flex justify-content-end gap-2 mt-3"> {/* Added mt-3 for spacing */}
                      <Button
                        color="secondary"
                        outline
                        className="rounded-pill px-4"
                        onClick={handleClear}
                        disabled={loading && !articleData}
                      >
                        {t("Clear")}
                      </Button>
                      <Button
                        color="info"
                        outline
                        className="rounded-pill px-4"
                        onClick={handleCopyJson}
                        disabled={!articleData}
                      >
                        {t("Copy as JSON")}
                      </Button>
                    </div>
                  </Form>

                  {error && (
                    <Alert
                      color="danger"
                      className="mt-4 rounded-3 shadow-sm border-0 d-flex align-items-center"
                      style={{ padding: "1rem 1.5rem" }}
                    >
                      <i className="ri-error-warning-line me-2 fs-5"></i>{" "}
                      {error}
                    </Alert>
                  )}

                  {articleData && (
                    <Card className="mt-4 rounded-3 shadow-lg border-0">
                      <CardHeader className="bg-light rounded-top-3 p-3">
                        <h4 className="mb-0 text-primary fs-5 fw-semibold">
                          {t("Scraped Article Data")}
                        </h4>
                      </CardHeader>
                      <CardBody className="p-4">
                        {articleData.imageUrl && (
                          <div className="mb-4 text-center">
                            <img
                              src={articleData.imageUrl}
                              alt="Article Preview"
                              className="img-fluid rounded-3 shadow-sm"
                              style={{ maxWidth: "100%", maxHeight: "400px", objectFit: "cover" }}
                            />
                          </div>
                        )}
                        <h3 className="card-title text-dark mb-3">
                          {articleData.title}
                        </h3>
                        <p className="card-text text-muted mb-1">
                          <i className="ri-user-line me-2"></i>
                          <strong>{t("Author:")}</strong> {articleData.author}
                        </p>
                        <p className="card-text text-muted mb-1">
                          <i className="ri-calendar-line me-2"></i>
                          <strong>{t("Published Date:")}</strong>{" "}
                          {articleData.publishedDate}
                        </p>
                        <p className="card-text text-muted mb-3">
                          <i className="ri-link me-2"></i>
                          <strong>{t("Source:")}</strong>{" "}
                          <a
                            href={articleData.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary text-decoration-none"
                          >
                            {articleData.sourceUrl}
                          </a>
                        </p>
                        <hr className="my-4" />
                        <div>
                          <h6 className="fw-semibold mb-3">{t("Content:")}</h6>
                          <p className="text-secondary line-height-base">
                            {renderContent(articleData.content)} {/* Use renderContent here */}
                          </p>
                        </div>
                        {/* Save and Delete Buttons */}
                        <div className="d-flex justify-content-end gap-2 mt-4">
                          <Button
                            color="success"
                            className="rounded-pill px-4"
                            onClick={handleSave}
                            disabled={!articleData}
                          >
                            {t("Save")}
                          </Button>
                          <Button
                            color="danger"
                            className="rounded-pill px-4"
                            onClick={handleDelete}
                            disabled={!articleData}
                          >
                            {t("Delete")}
                          </Button>
                        </div>
                      </CardBody>
                    </Card>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default NewsDataScraper;